import { Component, inject, ChangeDetectionStrategy } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import {
  distinctUntilChanged,
  filter,
  map,
  Subject,
  switchMap
} from 'rxjs';
import { select, Store } from '@ngrx/store';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoLocaleModule } from '@jsverse/transloco-locale';
import {
  ZefAvatarModule,
  ZefReactiveComponentBase
} from '@vshosting/core';
import { TASK_QUEUE_STATUS_COLOR_MAP } from '@vshosting/models';
import { StatusIconComponent, VshcDialogWrapperComponent } from '@vshosting/components';
import {
  NodeTaskQueueConsolidatedEntity,
  DomainEntity
} from '@vshosting/cdn/core';
import { FEATURE_NAME } from './warm-log-dialog.constant';
import { selectVshcDialogState, vshcDialogClose } from '@vshosting/core';

@Component({
    selector: 'vshcdn-warm-log-dialog',
    templateUrl: './warm-log-dialog.feature.html',
    styleUrls: ['./warm-log-dialog.feature.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    TranslocoLocaleModule,
    ZefAvatarModule,
    StatusIconComponent,
    VshcDialogWrapperComponent
]
})
export class WarmLogDialogFeature extends ZefReactiveComponentBase {

  // # Deps
  #store = inject(Store);
  #nodeTaskQueueConsolidatedEntity = inject(NodeTaskQueueConsolidatedEntity);
  #translocoService = inject(TranslocoService);
  #domainEntity = inject(DomainEntity);
  taskQueueStatusColorMap = TASK_QUEUE_STATUS_COLOR_MAP;


  // # Event streams
  onClose$ = new Subject<void>();

  // # Data
  // -- sync
  columnsToDisplay = [
    'status',
    'taskStarted',
    'taskFinished',
    'userFullName',
    'configData'
  ];

  // -- async
  state$ = this.#store.pipe(select(selectVshcDialogState(FEATURE_NAME)));
  open$ = this.state$.pipe(map((d) => d?.state));
  id$ = this.state$.pipe(map((d) => d?.meta));
  warmLog$ = this.id$.pipe(
    filter((d) => !!d),
    distinctUntilChanged(),
    switchMap((id) => this.#nodeTaskQueueConsolidatedEntity.list$({
      name: FEATURE_NAME,
      id
    }))
  );
  domainId$ = this.state$.pipe(
    map((d) => d?.meta),
    filter((d) => !!d),
    distinctUntilChanged()
  );
  domain$ = this.domainId$.pipe(
    switchMap((id) => this.#domainEntity.entityById$(id)),
    filter((d) => !!d)
  );
  translations$ = this.domain$.pipe(
    filter((d) => !!d),
    switchMap((domain) => this.#translocoService.selectTranslateObject(
      'warmLogDialog',
      {
        title: { domain: domain.proxyDomain }
      }
    ))
  );
  generalTranslations$ = this.#translocoService.selectTranslateObject('general');

  // # Resolver
  state = this.$connect({
    open: this.open$,
    warmLog: this.warmLog$,
    translations: this.translations$,
    generalTranslations: this.generalTranslations$
  });

  // # Action streams
  #closeAction$ = this.onClose$.pipe(
    map(() => vshcDialogClose({ key: FEATURE_NAME }))
  );

  constructor() {
    super();

    // # Dispatcher
    this.$dispatchActions([ this.#closeAction$ ]);
  }

}
